<script>
import { mapState } from 'vuex';
import ROBSvc from '@/services/ROBSvc';
import RobTable from './components/RobTable.vue';
import RobDetail from './components/RobDetail.vue';
import RobFormModal from './components/RobFormModal.vue';
import AdjustmentFormModal from './components/AdjustmentFormModal.vue';
import NoteModal from './components/NoteModal.vue';

export default {
  name: 'RemainOnBoard',
  components: {
    RobTable,
    RobDetail,
    RobFormModal,
    AdjustmentFormModal,
    NoteModal,
  },

  data() {
    return {
      // Asset Selection
      selectedAsset: null,

      // Main View State
      currentView: 'table', // 'table' or 'detail'

      // ROB Master Data
      robLoading: false,
      robError: null,
      robData: null,
      robReqId: 0, // To prevent race conditions
      confirmingDeleteId: null,

      // Pagination
      pagination: {
        current: 1,
        pageSize: 20,
        totalSize: 0,
      },

      // ROB Detail Data
      selectedRobDetail: null,
      detailLoading: false,
      detailError: null,
      isSummaryLoading: false, // State for loading summary log only

      // Adjustment Data
      adjustmentData: null,
      adjustmentLoading: false,
      adjustmentError: null,
      confirmingDeleteAdjustmentId: null,

      // Modal Visibility
      isAddModalVisible: false,
      isEditModalVisible: false,
      isAddAdjustmentModalVisible: false,
      isEditAdjustmentModalVisible: false,
      isNoteModalVisible: false,

      // Form Data
      editingRobData: null,
      editingAdjustmentData: null,
      selectedNote: '',
      isSubmitting: false,
    };
  },

  computed: {
    ...mapState('asset', ['assets']),
    selectedAssetData() {
      return this.assets.find((a) => a.massId === this.selectedAsset) || null;
    },
  },

  watch: {
    assets: {
      immediate: true,
      handler(list) {
        if (Array.isArray(list) && list.length > 0 && this.selectedAsset === null && list.length === 1) {
          // Only auto-select if there's exactly one asset
          this.selectedAsset = list[0].massId;
        }
      },
    },
    selectedAsset: {
      immediate: true,
      handler(newVal) {
        this.showTableView(); // Reset to table view on asset change
        this.pagination.current = 1; // Reset to first page
        if (!newVal) {
          // Fetch all data without filtering
          this.fetchRemainOnBoard(null);
          return;
        }
        this.fetchRemainOnBoard(newVal);
      },
    },
  },

  methods: {
    // DATA FETCHING
    async fetchRemainOnBoard(massId) {
      this.robLoading = true;
      this.robError = null;
      this.confirmingDeleteId = null;
      const reqId = ++this.robReqId;
      try {
        const params = {
          'orders[mrobStartTime]': 'DESC',
          page: this.pagination.current,
          max: this.pagination.pageSize,
        };

        if (massId) {
          params['finds[mrobMassId]'] = massId;
        } else if (this.assets.length > 0) {
          // When "All Assets" is selected, send all asset IDs as array
          // Format: finds[mrobMass][]=68&finds[mrobMass][]=92
          this.assets.forEach((asset) => {
            if (!params['finds[mrobMassId]']) {
              params['finds[mrobMassId]'] = [];
            }
            params['finds[mrobMassId]'].push(asset.massId);
          });
        }

        const res = await ROBSvc.master.get({ params });
        if (reqId === this.robReqId) {
          this.robData = res?.data?.data ?? null;
          this.pagination.totalSize = res?.data?.pagination?.totalData ?? 0;
        }
      } catch (err) {
        if (reqId === this.robReqId) {
          this.robError = err;
          this.robData = null;
          this.pagination.totalSize = 0;
        }
      } finally {
        if (reqId === this.robReqId) {
          this.robLoading = false;
        }
      }
    },
    async fetchRobDetail(rob, summaryInterval = 'daily') {
      this.detailLoading = true;
      this.detailError = null;
      this.selectedRobDetail = null;
      try {
        const summaryRes = await ROBSvc.summary.get(rob.mrobId, { params: { interval: summaryInterval } });
        this.selectedRobDetail = summaryRes?.data?.data ?? null;
        // Fetch adjustments concurrently
        this.fetchAdjustments(rob.mrobId);
      } catch (error) {
        console.error('Failed to fetch ROB details:', error);
        this.detailError = 'Failed to fetch details. Please try again.';
      } finally {
        this.detailLoading = false;
      }
    },
    async fetchAdjustments(mrobId) {
      this.adjustmentLoading = true;
      this.adjustmentError = null;
      try {
        const res = await ROBSvc.adjustment.get({ params: { 'finds[robaMrobId]': mrobId } });
        this.adjustmentData = res?.data?.data ?? null;
      } catch (error) {
        console.error('Failed to fetch adjustments:', error);
        this.adjustmentError = 'Failed to fetch adjustments.';
      } finally {
        this.adjustmentLoading = false;
      }
    },

    // NAVIGATION
    showTableView() {
      this.currentView = 'table';
      this.selectedRobDetail = null;
      this.detailError = null;
      this.adjustmentData = null;
    },
    async handleViewDetails(rob) {
      this.currentView = 'detail';
      this.fetchRobDetail(rob);
    },
    async handleSummaryIntervalChange(interval) {
      if (!this.selectedRobDetail?.master?.mrobId) return;

      this.isSummaryLoading = true;
      this.detailError = null;
      try {
        const mrobId = this.selectedRobDetail.master.mrobId;
        const res = await ROBSvc.summary.get(mrobId, { params: { interval } });
        // Update the detail data without triggering the main loader
        this.selectedRobDetail = res?.data?.data ?? this.selectedRobDetail;
      } catch (error) {
        console.error('Failed to re-fetch ROB summary:', error);
        this.detailError = 'Failed to refresh summary. Please try again.';
      } finally {
        this.isSummaryLoading = false;
      }
    },

    // ROB CRUD HANDLERS
    handleAddNewRob() {
      this.editingRobData = null;
      this.isAddModalVisible = true;
    },
    handleEditRob(rob) {
      const localStartTime = rob.mrobStartTime ? new Date(`${rob.mrobStartTime.replace(' ', 'T')}Z`) : null;
      const localEndTime = rob.mrobEndTime ? new Date(`${rob.mrobEndTime.replace(' ', 'T')}Z`) : null;
      this.editingRobData = {
        mrobId: rob.mrobId,
        startTime: localStartTime,
        endTime: localEndTime,
        initialValue: rob.mrobInitialValue,
      };
      this.isEditModalVisible = true;
    },
    async handleRobFormSubmit(formData) {
      this.isSubmitting = true;
      const startTime = new Date(formData.startTime);
      startTime.setSeconds(0, 0);
      const endTime = new Date(formData.endTime);
      endTime.setSeconds(59, 0);

      const payload = {
        mrobMassId: this.selectedAsset,
        mrobStartTime: this.convertToGMT0String(startTime),
        mrobEndTime: this.convertToGMT0String(endTime),
        mrobInitialValue: Number(formData.initialValue),
      };

      try {
        if (formData.mrobId) { // Update
          await ROBSvc.master.update(formData.mrobId, payload);
        } else { // Create
          await ROBSvc.master.create(payload);
        }
        this.isAddModalVisible = false;
        this.isEditModalVisible = false;
        this.pagination.current = 1; // Reset to first page
        await this.fetchRemainOnBoard(this.selectedAsset);
      } catch (error) {
        console.error('Failed to save ROB profile:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    async handleDeleteRob(rob) {
      if (this.confirmingDeleteId === rob.mrobId) {
        try {
          await ROBSvc.master.delete(rob.mrobId);
          this.pagination.current = 1; // Reset to first page
          await this.fetchRemainOnBoard(this.selectedAsset);
        } catch (error) {
          console.error('Failed to delete ROB profile:', error);
        } finally {
          this.confirmingDeleteId = null;
        }
      } else {
        this.confirmingDeleteId = rob.mrobId;
      }
    },

    // ADJUSTMENT CRUD HANDLERS
    handleAddAdjustment() {
      this.editingAdjustmentData = null;
      this.isAddAdjustmentModalVisible = true;
    },
    handleEditAdjustment(adj) {
      this.editingAdjustmentData = {
        robaId: adj.robaId,
        robaType: adj.robaType,
        robaTime: new Date(`${adj.robaTime.replace(' ', 'T')}Z`),
        robaValue: adj.robaValue,
        robaNote: adj.robaNote,
      };
      this.isEditAdjustmentModalVisible = true;
    },
    async handleAdjustmentFormSubmit(formData) {
      this.isSubmitting = true;
      const time = new Date(formData.robaTime);
      time.setSeconds(0, 0);

      const payload = {
        robaMrobId: this.selectedRobDetail.master.mrobId,
        robaType: Number(formData.robaType),
        robaTime: this.convertToGMT0String(time),
        robaValue: Number(formData.robaValue),
        robaNote: formData.robaNote,
      };

      try {
        if (formData.robaId) { // Update
          await ROBSvc.adjustment.update(formData.robaId, payload);
        } else { // Create
          await ROBSvc.adjustment.create(payload);
        }
        this.isAddAdjustmentModalVisible = false;
        this.isEditAdjustmentModalVisible = false;
        // Refresh both details and adjustments
        await this.fetchRobDetail(this.selectedRobDetail.master);
      } catch (error) {
        console.error('Failed to save adjustment:', error);
      } finally {
        this.isSubmitting = false;
      }
    },
    async handleDeleteAdjustment(adj) {
      if (this.confirmingDeleteAdjustmentId === adj.robaId) {
        try {
          await ROBSvc.adjustment.delete(adj.robaId);
          await this.fetchRobDetail(this.selectedRobDetail.master); // Refresh
        } catch (error) {
          console.error('Failed to delete adjustment:', error);
        } finally {
          this.confirmingDeleteAdjustmentId = null;
        }
      } else {
        this.confirmingDeleteAdjustmentId = adj.robaId;
      }
    },

    // NOTE MODAL
    handleShowNote(note) {
      this.selectedNote = note;
      this.isNoteModalVisible = true;
    },

    // UTILITY
    convertToGMT0String(localDate) {
      if (!localDate) return null;
      return new Date(localDate).toISOString().slice(0, 19).replace('T', ' ');
    },
    handlePageChanged(page) {
      this.pagination.current = page;
      this.fetchRemainOnBoard(this.selectedAsset);
    },
  },

  mounted() {
    this.$store.dispatch('asset/getAssetsData');
  },
};
</script>

<template>
  <b-container fluid v-if="canRead('master_rob_rob')">
    <div class="iq-card my-3 px-3 pb-3">
      <div class="iq-header-title py-3">
        <h4 class="card-title">Remain On Board</h4>
        <b-form-group label="Asset" label-for="asset-select" class="mb-0" style="max-width: 300px;">
          <b-form-select id="asset-select" v-model="selectedAsset">
            <b-form-select-option :value="null">All Assets</b-form-select-option>
            <b-form-select-option v-for="asset in assets" :key="asset.massId" :value="asset.massId">
              {{ asset.massName }} ({{ asset.massId }})
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
      </div>
      <hr style="border-top: 2px solid #E8ECF3;" />

      <div v-if="selectedAsset !== null || assets.length > 0">
        <!-- Table View -->
        <rob-table
          v-if="currentView === 'table'"
          :items="robData"
          :loading="robLoading"
          :error="robError"
          :selected-asset-data="selectedAssetData"
          :confirming-delete-id="confirmingDeleteId"
          :pagination="pagination"
          @add-new="handleAddNewRob"
          @view-details="handleViewDetails"
          @edit-rob="handleEditRob"
          @delete-rob="handleDeleteRob"
          @page-changed="handlePageChanged"
          @update:confirmingDeleteId="confirmingDeleteId = $event"
        />

        <!-- Detail View -->
        <rob-detail
          v-if="currentView === 'detail'"
          :selected-rob-detail="selectedRobDetail"
          :detail-loading="detailLoading"
          :detail-error="detailError"
          :adjustment-data="adjustmentData"
          :adjustment-loading="adjustmentLoading"
          :adjustment-error="adjustmentError"
          :summary-loading="isSummaryLoading"
          :confirming-delete-adjustment-id.sync="confirmingDeleteAdjustmentId"
          @back="showTableView"
          @add-adjustment="handleAddAdjustment"
          @edit-adjustment="handleEditAdjustment"
          @delete-adjustment="handleDeleteAdjustment"
          @show-note="handleShowNote"
          @summary-interval-change="handleSummaryIntervalChange"
        />
      </div>
    </div>

    <!-- Modals -->
    <rob-form-modal
      :visible.sync="isAddModalVisible"
      :is-submitting="isSubmitting"
      @submit="handleRobFormSubmit"
    />
    <rob-form-modal
      :visible.sync="isEditModalVisible"
      :is-submitting="isSubmitting"
      :initial-data="editingRobData"
      is-edit-mode
      @submit="handleRobFormSubmit"
    />
    <adjustment-form-modal
      :visible.sync="isAddAdjustmentModalVisible"
      :is-submitting="isSubmitting"
      @submit="handleAdjustmentFormSubmit"
    />
    <adjustment-form-modal
      :visible.sync="isEditAdjustmentModalVisible"
      :is-submitting="isSubmitting"
      :initial-data="editingAdjustmentData"
      is-edit-mode
      @submit="handleAdjustmentFormSubmit"
    />
    <note-modal
      :visible.sync="isNoteModalVisible"
      :note="selectedNote"
    />
  </b-container>
</template>
